/**
 * Export Utilities for Collections
 * Provides functionality to export collection case data to Excel format
 */

import { type CollectionCase } from "@/types/collections";
import { format } from "date-fns";

/**
 * Export collection cases to Excel (CSV format)
 * @param cases - Array of collection cases to export
 * @param filename - Optional filename (defaults to my-cases-{timestamp}.csv)
 */
export function exportCollectionCasesToExcel(
  cases: CollectionCase[],
  filename?: string
): void {
  if (cases.length === 0) {
    throw new Error("No cases to export");
  }

  // Define columns to export
  const columns = [
    { key: "caseId", label: "Case ID" },
    { key: "customerName", label: "Customer Name" },
    { key: "customerEmail", label: "Customer Email" },
    { key: "customerPhone", label: "Customer Phone" },
    { key: "priority", label: "Priority" },
    { key: "status", label: "Status" },
    { key: "baseAmount", label: "Amount" },
    { key: "daysInCollection", label: "Days in Collection" },
    { key: "nextAction", label: "Next Action" },
    { key: "nextActionDate", label: "Action Due Date" },
    { key: "strategy", label: "Strategy" },
    { key: "assignedTo", label: "Assigned To" },
    { key: "createdAt", label: "Created At" },
    { key: "updatedAt", label: "Updated At" },
    { key: "notes", label: "Notes" },
  ];

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = cases.map((caseItem) => {
    return columns
      .map((col) => {
        const key = col.key as keyof CollectionCase;
        let value: string | number | Date | undefined | null = caseItem[key] as string | number | Date | undefined | null;

        // Format specific fields
        if (key === "nextActionDate" || key === "createdAt" || key === "updatedAt") {
          value = value ? format(new Date(value), "MMM d, yyyy") : "";
        } else if (key === "baseAmount") {
          value = value != null ? `$${(value as number).toLocaleString()}` : "";
        } else if (key === "strategy") {
          value = caseItem.strategy?.name || "";
        } else if (value === undefined || value === null) {
          value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0] + 
                    "_" + new Date().toTimeString().split(" ")[0].replace(/:/g, "-");
  const defaultFilename = `my-cases-${timestamp}.csv`;

  link.setAttribute("href", url);
  link.setAttribute("download", filename || defaultFilename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Calculate totals for collection cases
 */
export interface CollectionCaseTotals {
  count: number;
  totalAmount: number;
  highPriority: number;
  mediumPriority: number;
  lowPriority: number;
  avgDaysInCollection: number;
}

export function calculateCollectionCaseTotals(
  cases: CollectionCase[]
): CollectionCaseTotals {
  if (cases.length === 0) {
    return {
      count: 0,
      totalAmount: 0,
      highPriority: 0,
      mediumPriority: 0,
      lowPriority: 0,
      avgDaysInCollection: 0,
    };
  }

  const totalAmount = cases.reduce((sum, c) => sum + (c.baseAmount ?? 0), 0);
  const highPriority = cases.filter((c) => c.priority === "High").length;
  const mediumPriority = cases.filter((c) => c.priority === "Medium").length;
  const lowPriority = cases.filter((c) => c.priority === "Low").length;
  const avgDaysInCollection = Math.round(
    cases.reduce((sum, c) => sum + c.daysInCollection, 0) / cases.length
  );

  return {
    count: cases.length,
    totalAmount,
    highPriority,
    mediumPriority,
    lowPriority,
    avgDaysInCollection,
  };
}
